import React, { useCallback, useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Button,
  Card,
  CardMedia,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  PhotoCamera as CameraIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  CameraAlt as CaptureIcon,
  Close as CloseIcon,
  FlipCameraAndroid as SwitchCameraIcon,
} from '@mui/icons-material';
import { livresAPI } from '../../services/api';
import { useTranslation } from 'react-i18next';

const PhotoUpload = forwardRef(({ 
  onPhotoUploaded, 
  onOCRResult, 
  acceptedTypes = ['image/jpeg', 'image/png', 'image/webp'],
  maxSize = 10 * 1024 * 1024, // 10MB
  multiple = false,
  label = "Ajouter une photo",
  singlePhotoMode = false, // Mode avec une seule photo visible (remplace l'ancienne)
  hidePreview = false // Cacher l'affichage des photos uploadées
}, ref) => {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [ocrResults, setOcrResults] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, files: null });

  // Exposer la méthode handleCameraCapture au parent via ref
  useImperativeHandle(ref, () => ({
    triggerCamera: handleCameraCapture
  }));
  
  // Fonction helper pour ajouter un fichier en respectant le mode single/multiple
  const addUploadedFile = useCallback((fileData) => {
    setUploadedFiles(prev => {
      if (singlePhotoMode) {
        // Nettoyer l'ancienne photo
        if (prev.length > 0 && prev[0].preview) {
          URL.revokeObjectURL(prev[0].preview);
        }
        return [fileData];
      }
      return [...prev, fileData];
    });
  }, [singlePhotoMode]);
  
  // Fonction helper pour ajouter un résultat OCR
  const addOcrResult = useCallback((ocrResult) => {
    setOcrResults(prev => singlePhotoMode ? [ocrResult] : [...prev, ocrResult]);
  }, [singlePhotoMode]);
  
  // États pour la caméra
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [facingMode, setFacingMode] = useState('environment'); // 'environment' (arrière) ou 'user' (avant)
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Fonction de nettoyage de la caméra
  const stopCamera = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setCameraError(null);
  }, [cameraStream]);

  // Nettoyage au démontage du composant
  useEffect(() => {
    return () => {
      stopCamera();
      // Nettoyer les URLs de prévisualisation
      uploadedFiles.forEach(file => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, [stopCamera, uploadedFiles]);

  // Démarrer la vidéo quand le stream est disponible
  useEffect(() => {
    if (cameraStream && videoRef.current) {
      videoRef.current.srcObject = cameraStream;
    }
  }, [cameraStream]);

  // Fonction pour traiter les fichiers uploadés
  const processFiles = useCallback(async (filesToProcess) => {
    setUploading(true);

    for (const file of filesToProcess) {
      try {
        const formData = new FormData();
        formData.append('photo', file);

        // Upload et OCR
        const response = await livresAPI.uploadPhoto(formData);
        const result = response.data.data;

        // Ajouter à la liste des fichiers uploadés
        const fileData = {
          ...result,
          file,
          preview: URL.createObjectURL(file)
        };

        addUploadedFile(fileData);

        // Callback avec les données du fichier
        if (onPhotoUploaded) {
          onPhotoUploaded(fileData);
        }

        // Callback avec les résultats OCR
        if (result.ocr && result.ocr.success && onOCRResult) {
          addOcrResult(result.ocr);
          onOCRResult(result.ocr);
        }

        // Pas de toast de succès - la photo apparaît, c'est suffisant
      } catch (error) {
        console.error('Erreur upload:', error);
        setAlerts(prev => [...prev, {
          severity: 'error',
          message: t('photoUpload.uploadError', { fileName: file.name })
        }]);
      }
    }
    
    setUploading(false);
  }, [onPhotoUploaded, onOCRResult, addUploadedFile, addOcrResult]);

  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    // Gestion des fichiers rejetés
    if (rejectedFiles.length > 0) {
      rejectedFiles.forEach(rejection => {
        const { file, errors } = rejection;
        errors.forEach(error => {
          switch (error.code) {
            case 'file-too-large':
              setAlerts(prev => [...prev, {
                severity: 'error',
                message: t('photoUpload.fileTooLarge', { fileName: file.name })
              }]);
              break;
            case 'file-invalid-type':
              setAlerts(prev => [...prev, {
                severity: 'error',
                message: t('photoUpload.invalidFormat', { fileName: file.name })
              }]);
              break;
            case 'too-many-files':
              setAlerts(prev => [...prev, {
                severity: 'error',
                message: t('photoUpload.tooManyFiles')
              }]);
              break;
            default:
              setAlerts(prev => [...prev, {
                severity: 'error',
                message: `${file.name}: ${error.message}`
              }]);
          }
        });
      });
    }

    if (acceptedFiles.length === 0) return;

    // Si en mode single photo et qu'une photo existe déjà, demander confirmation
    if (singlePhotoMode && uploadedFiles.length > 0) {
      setConfirmDialog({ open: true, files: acceptedFiles });
      return;
    }

    // Sinon, traiter directement les fichiers
    await processFiles(acceptedFiles);
  }, [singlePhotoMode, uploadedFiles.length, processFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedTypes.reduce((acc, type) => {
      acc[type] = [];
      return acc;
    }, {}),
    maxSize,
    multiple,
    disabled: uploading
  });

  const removeFile = (index) => {
    setUploadedFiles(prev => {
      const newFiles = [...prev];
      // Révoquer l'URL de prévisualisation
      if (newFiles[index].preview) {
        URL.revokeObjectURL(newFiles[index].preview);
      }
      newFiles.splice(index, 1);
      return newFiles;
    });
    
    setOcrResults(prev => {
      const newResults = [...prev];
      newResults.splice(index, 1);
      return newResults;
    });
  };

  // Fonctions de gestion de la caméra
  const startCamera = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraError(t('photoUpload.cameraNotAvailable'));
      return;
    }

    try {
      setCameraError(null);
      
      // Détecter si on est sur mobile
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      // Configuration adaptée pour mobile et iOS
      const constraints = {
        video: {
          width: { ideal: isMobile ? 640 : 1280, max: 1920 },
          height: { ideal: isMobile ? 480 : 720, max: 1080 },
          facingMode: isMobile ? { ideal: facingMode } : facingMode
        },
        audio: false
      };

      console.log('📱 Détection mobile:', isMobile, 'iOS:', isIOS);
      console.log('📷 Contraintes caméra:', constraints);

      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        
        // Pour iOS/Safari, forcer le démarrage de la vidéo
        if (isIOS) {
          videoRef.current.setAttribute('playsinline', true);
          videoRef.current.setAttribute('webkit-playsinline', true);
        }
        
        // Attendre que la vidéo soit prête avant de jouer
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play().catch(e => {
            console.warn('⚠️ Autoplay bloqué, interaction utilisateur requise:', e);
          });
        };
      }
    } catch (error) {
      console.error('❌ Erreur accès caméra:', error);
      let errorMessage = 'Erreur inconnue';
      
      switch (error.name) {
        case 'NotAllowedError':
          errorMessage = t('photoUpload.cameraAccessDenied');
          break;
        case 'NotFoundError':
          errorMessage = t('photoUpload.noCameraFound');
          break;
        case 'NotSupportedError':
          errorMessage = t('photoUpload.cameraNotSupported');
          break;
        case 'OverconstrainedError':
          errorMessage = t('photoUpload.cameraConfigError');
          break;
        case 'SecurityError':
          errorMessage = t('photoUpload.cameraSecurityError');
          break;
        default:
          errorMessage = t('photoUpload.cameraUnknownError', { message: error.message || 'Impossible d\'accéder à la caméra' });
      }
      
      setCameraError(errorMessage);
    }
  };


  const handleCameraCapture = () => {
    // Si en mode single photo et qu'une photo existe déjà, demander confirmation
    if (singlePhotoMode && uploadedFiles.length > 0) {
      setConfirmDialog({ open: true, files: 'camera' });
      return;
    }
    
    // Ouvrir la caméra directement
    openCamera();
  };

  const openCamera = () => {
    // Vérifier HTTPS sur mobile
    const isSecure = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (!isSecure && isMobile) {
      setCameraError(t('photoUpload.cameraHttpsRequired'));
      setCameraOpen(true);
      return;
    }
    
    setCameraOpen(true);
    startCamera();
  };

  const closeCameraModal = () => {
    stopCamera();
    setCameraOpen(false);
  };

  const switchCamera = async () => {
    // Arrêter le flux actuel
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
    }
    
    // Basculer entre front et back
    const newFacingMode = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(newFacingMode);
    
    // Redémarrer avec la nouvelle caméra
    setTimeout(() => {
      startCamera();
    }, 100);
  };


  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Configurer le canvas avec les dimensions de la vidéo
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Dessiner la frame actuelle de la vidéo sur le canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convertir en blob
    canvas.toBlob(async (blob) => {
      if (!blob) return;

      // Créer un fichier à partir du blob
      const file = new File([blob], `photo-${Date.now()}.jpg`, {
        type: 'image/jpeg'
      });

      // Fermer la caméra
      closeCameraModal();

      // Traiter le fichier capturé (la confirmation a déjà été demandée avant d'ouvrir la caméra)
      await processFiles([file]);
    }, 'image/jpeg', 0.8);
  };

  return (
    <Box>
      {/* Alertes d'erreur */}
      {alerts.length > 0 && (
        <Box sx={{ mb: 2 }}>
          {alerts.map((alert, index) => (
            <Alert
              key={index}
              severity={alert.severity}
              onClose={() => setAlerts(prev => prev.filter((_, i) => i !== index))}
              sx={{ mb: 1 }}
            >
              {alert.message}
            </Alert>
          ))}
        </Box>
      )}
      
      {uploading ? (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="body1">{t('photoUpload.uploading')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('photoUpload.processingOcr')}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Bouton principal : Caméra (le plus important) */}
          <Button
            variant="contained"
            size="large"
            startIcon={<CameraIcon />}
            onClick={handleCameraCapture}
            disabled={uploading}
            fullWidth
            sx={{ 
              py: 2, 
              fontSize: '1.1rem',
              borderRadius: 2,
            }}
          >
            {t('photoUpload.takePhoto')}
          </Button>
          
          {/* Zone de drop réduite */}
          <Paper
            {...getRootProps()}
            sx={{
              p: 1.5,
              border: '1px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.400',
              backgroundColor: isDragActive ? 'primary.light' : 'transparent',
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'all 0.3s ease',
              borderRadius: 2,
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'grey.50',
              }
            }}
          >
            <input {...getInputProps()} />
            
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <UploadIcon sx={{ fontSize: 20, color: 'grey.500' }} />
              <Typography variant="body2" color="text.secondary">
                {isDragActive 
                  ? t('photoUpload.dropHere')
                  : t('photoUpload.dragAndDrop')
                }
              </Typography>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Fichiers uploadés */}
      {uploadedFiles.length > 0 && !hidePreview && (
        <Box sx={{ mt: 3 }}>
          <Grid container spacing={2}>
            {uploadedFiles.map((fileData, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card>
                  <CardMedia
                    component="img"
                    height="200"
                    image={fileData.preview}
                    alt={fileData.originalName}
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                      {fileData.originalName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {(fileData.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                    {fileData.filename && (
                      <Typography variant="caption" color="success.main" display="block">
                        {t('photoUpload.uploadedSuccess')}
                      </Typography>
                    )}
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => window.open(fileData.preview, '_blank')}
                          title={t('photoUpload.viewImage')}
                        >
                          <ViewIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeFile(index)}
                          title={t('photoUpload.delete')}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                      {fileData.ocr && (
                        <Typography variant="caption" color="primary.main">
                          📖 OCR: {(fileData.ocr.confidence * 100).toFixed(0)}%
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Modal de capture caméra */}
      <Dialog 
        open={cameraOpen} 
        onClose={(event, reason) => {
          // Empêcher la fermeture par clic en dehors si la caméra est active
          console.log("onClose", reason);
          if (reason === 'backdropClick' && cameraStream) {
            const shouldClose = window.confirm(t('photoUpload.closeCameraConfirm'));
            if (!shouldClose) {
              return;
            }
          }
          closeCameraModal();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="div">{t('photoUpload.capturePhoto')}</Typography>
          <IconButton onClick={closeCameraModal}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center' }}>
            {cameraError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {cameraError}
              </Alert>
            ) : (
              <>
                <Box 
                  sx={{ 
                    position: 'relative',
                    width: '100%',
                    maxWidth: 640,
                    mx: 'auto',
                    mb: 2
                  }}
                >
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: 8,
                      backgroundColor: '#000'
                    }}
                  />
                  {/* Canvas caché pour la capture */}
                  <canvas
                    ref={canvasRef}
                    style={{ display: 'none' }}
                  />
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('photoUpload.positionBook')}
                </Typography>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', pb: 3, px: 3 }}>
          {!cameraError && cameraStream && (
            <Button
              variant="outlined"
              startIcon={<SwitchCameraIcon />}
              onClick={switchCamera}
              disabled={uploading}
            >
              {facingMode === 'environment' ? t('photoUpload.backCamera') : t('photoUpload.frontCamera')}
            </Button>
          )}
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            {!cameraError && cameraStream && (
              <Button
                variant="contained"
                size="large"
                startIcon={<CaptureIcon />}
                onClick={capturePhoto}
                disabled={uploading}
                sx={{ minWidth: 150 }}
              >
                {uploading ? t('photoUpload.processing') : t('photoUpload.capture')}
              </Button>
            )}
            <Button onClick={closeCameraModal} size="large">
              {t('photoUpload.cancel')}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmation de remplacement */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, files: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('photoUpload.replacePhoto')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('photoUpload.photoAlreadyPresent', { 
              context: confirmDialog.files === 'camera' 
                ? t('photoUpload.replaceByCamera') 
                : t('photoUpload.replaceByNewPhoto')
            })}
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t('photoUpload.photoWillBeReplaced')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ open: false, files: null })}
            startIcon={<CloseIcon />}
          >
            {t('photoUpload.cancel')}
          </Button>
          <Button
            onClick={async () => {
              const filesToProcess = confirmDialog.files;
              setConfirmDialog({ open: false, files: null });
              
              if (filesToProcess === 'camera') {
                // Ouvrir la caméra
                openCamera();
              } else if (filesToProcess) {
                // Traiter les fichiers
                await processFiles(filesToProcess);
              }
            }}
            variant="contained"
            color="primary"
            autoFocus
          >
            {t('photoUpload.replace')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

PhotoUpload.displayName = 'PhotoUpload';

export default PhotoUpload;
